/**
 * Phase 2c — Cmd+K command palette for /docs/*.
 *
 * Backed by GET /api/wiki/search (Atlas Search → regex fallback). Debounced
 * 180ms, capped at 12 hits, full keyboard nav (↑/↓/↵/Esc).
 *
 * Visual: matches the Swiss/high-contrast docs aesthetic — dark navy panel,
 * cyan eyebrow accents, IBM Plex Mono metadata, asymmetric reveal animation.
 *
 * Phase 12.2c — Each result now wears its parent-section's brand color
 * as a left-edge bar + tinted result-arrow on hover. Section is derived
 * client-side by walking the nav tree (passed in as a prop) for the
 * result's slug → finding its containing section title → looking up
 * the color in the shared sectionStyles map. Same identity rules as
 * the landing tiles + sidebar headings; users get a consistent "this
 * matches the regulator section" cue at a glance.
 *
 * Why a custom palette (not the shadcn Command primitive): the shadcn one
 * pulls in cmdk and a focus-trap library + assumes a Radix Dialog wrapper.
 * For ~120 lines of UI we keep the dependency surface clean.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, ArrowRight, Tag, Loader2 } from "lucide-react";
import { styleForSection, SECTION_STYLE_FALLBACK } from "../../lib/sectionStyles";

const API = process.env.REACT_APP_BACKEND_URL;


export default function DocsCmdKPalette({ open, onClose, navTree }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [source, setSource] = useState("");
  // Phase 12.2g — section filter chip state. "" means All.
  const [sectionFilter, setSectionFilter] = useState("");
  const inputRef = useRef(null);
  const nav = useNavigate();

  // Focus the input when the palette opens, reset state when it closes.
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    } else {
      setQ("");
      setResults([]);
      setActiveIdx(0);
      setSource("");
    }
  }, [open]);

  // Debounced search — 180ms after the last keystroke.
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (!trimmed) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = window.setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/api/wiki/search`, { params: { q: trimmed } });
        setResults(r.data?.results || []);
        setSource(r.data?.source || "");
        setActiveIdx(0);
      } catch (_e) {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => window.clearTimeout(timer);
  }, [q, open]);

  // Phase 12.2c — Build a slug→section-title map from the nav tree
  // once per nav update. Empty if nav is loading.
  const slugToSection = useMemo(() => {
    const out = {};
    for (const section of navTree || []) {
      for (const leaf of section.children || []) {
        if (leaf?.slug) out[leaf.slug] = section.title || "";
      }
    }
    return out;
  }, [navTree]);

  // Fallback when a slug isn't in the nav (e.g. an orphan page that
  // exists in MongoDB but isn't curated into `wiki_nav` yet). Maps
  // common slug prefixes / keywords to the same section names the
  // shared `styleForSection` map already recognises.
  const inferSection = (slug, title) => {
    const s = (slug || "").toLowerCase();
    const t = (title || "").toLowerCase();
    const has = (x) => s.startsWith(x) || s.includes("/" + x.replace("/", ""))
                    || t.includes(x.replace("/", "").replace("-", " "));
    if (has("getting-started") || has("audit-walkthrough") || has("faq")) return "Get Started";
    if (has("scanner")  || has("scan-lifecycle"))     return "Scanner";
    if (has("audits/")  || has("audit-lifecycle"))    return "Audits";
    if (has("atlas/"))                                return "Atlas";
    if (has("methodology") || has("glossary"))        return "Methodology";
    if (has("pqc/") || has("harvest-now") || has("preparing-for-2030"))
                                                       return "Post-Quantum Cryptography";
    if (has("compliance") || has("trust-center") || has("verify-a-certificate"))
                                                       return "Compliance";
    if (has("security/"))                              return "Security";
    if (has("billing"))                                return "Billing & Plans";
    if (has("team/"))                                  return "Team & Access";
    if (has("integrations"))                           return "Integrations";
    if (has("privacy") || has("data-retention"))       return "Privacy & Data";
    if (has("sdk-") || s.includes("developer"))        return "Developers";
    if (has("enterprise"))                             return "Enterprise";
    if (has("qelli"))                                  return "Qelli";
    if (has("changelog") || has("releases/"))          return "Releases";
    return "";
  };

  const resolveSectionAndAccent = (slug, title) => {
    const sectionTitle = slugToSection[slug] || inferSection(slug, title);
    if (!sectionTitle) return { sectionTitle: "", accent: SECTION_STYLE_FALLBACK.color };
    return { sectionTitle, accent: styleForSection(sectionTitle).color };
  };

  // Phase 12.2g — filter results by selected section chip. Empty
  // sectionFilter = no filter applied (the default "All" pill).
  const filteredResults = useMemo(() => {
    if (!sectionFilter) return results;
    return results.filter((r) => {
      const { sectionTitle } = resolveSectionAndAccent(r.slug, r.title);
      return sectionTitle === sectionFilter;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results, sectionFilter, slugToSection]);

  // The chip set is the unique list of section titles present in the
  // navTree, in nav order. Stable across queries — doesn't shift on
  // typing. Empty until nav loads.
  const sectionChips = useMemo(
    () => (navTree || [])
      .map((s) => ({
        title: s.title || "",
        color: styleForSection(s.title || "").color,
      }))
      .filter((c) => c.title),
    [navTree],
  );

  // Color the active chip's section into the panel border + active
  // row defaults so the mode shift is unmistakable. Falls back to cyan.
  const filterAccent = sectionFilter
    ? styleForSection(sectionFilter).color
    : null;

  // Keyboard nav — uses filteredResults so ↑/↓/↵ respect the chip filter.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, Math.max(0, filteredResults.length - 1)));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter") {
        const hit = filteredResults[activeIdx];
        if (hit) {
          e.preventDefault();
          onClose();
          nav(`/docs/${hit.slug}`);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filteredResults, activeIdx, nav, onClose]);

  // Reset the highlighted row whenever the chip filter changes so we
  // don't end up pointing past the end of the (now-shorter) list.
  useEffect(() => { setActiveIdx(0); }, [sectionFilter]);

  const empty = useMemo(
    () => !loading && q.trim() && filteredResults.length === 0,
    [loading, q, filteredResults],
  );

  if (!open) return null;

  return (
    <div
      className="qt-docs-cmdk-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="docs-cmdk-title"
      data-testid="docs-cmdk-palette"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="qt-docs-cmdk-panel"
        data-section-filter={sectionFilter || ""}
        style={filterAccent ? { "--panel-accent": filterAccent } : undefined}
      >
        <div className="qt-docs-cmdk-input-row">
          <Search size={14} style={{ color: filterAccent || "var(--qt-cyan)" }} />
          <input
            ref={inputRef}
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={
              sectionFilter
                ? `Search inside ${sectionFilter}…`
                : "Search QorTrace docs…"
            }
            aria-label="Search docs"
            id="docs-cmdk-title"
            data-testid="docs-cmdk-input"
            className="qt-docs-cmdk-input"
            autoComplete="off"
            spellCheck={false}
          />
          {loading && <Loader2 size={12} className="animate-spin" style={{ color: "var(--qt-text-3)" }} />}
          <kbd className="qt-docs-cmdk-kbd" aria-hidden>esc</kbd>
        </div>

        {/* Phase 12.2g — Section filter chips. "All" + one chip per
         * curated section, in nav order. Active chip tints the
         * panel accent and shifts the input placeholder. Clicking
         * the same chip a second time clears the filter. */}
        {sectionChips.length > 0 && (
          <div
            className="qt-docs-cmdk-chips"
            data-testid="docs-cmdk-section-chips"
          >
            <button
              type="button"
              data-testid="docs-cmdk-section-chip-all"
              className={`qt-docs-cmdk-chip ${!sectionFilter ? "is-active" : ""}`}
              onClick={() => setSectionFilter("")}
            >
              All
            </button>
            {sectionChips.map((c) => {
              const active = sectionFilter === c.title;
              return (
                <button
                  type="button"
                  key={c.title}
                  data-testid={`docs-cmdk-section-chip-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className={`qt-docs-cmdk-chip ${active ? "is-active" : ""}`}
                  style={{ "--chip-accent": c.color }}
                  onClick={() => setSectionFilter(active ? "" : c.title)}
                >
                  {c.title}
                </button>
              );
            })}
          </div>
        )}

        <div
          className="qt-docs-cmdk-results"
          data-testid="docs-cmdk-results"
          role="listbox"
          aria-label="Documentation search results"
        >
          {q.trim() === "" ? (
            <div className="qt-docs-cmdk-hint">
              <span className="qt-mono">Type to search · ↑↓ navigate · ↵ open · esc close</span>
            </div>
          ) : empty ? (
            <div className="qt-docs-cmdk-hint" data-testid="docs-cmdk-empty">
              <span>
                No matches for <strong>"{q}"</strong>
                {sectionFilter && (
                  <>
                    {" "}inside <strong>{sectionFilter}</strong>
                  </>
                )}.
              </span>
              {sectionFilter && (
                <button
                  type="button"
                  data-testid="docs-cmdk-empty-clear-filter"
                  className="qt-docs-cmdk-clear-filter"
                  onClick={() => setSectionFilter("")}
                >
                  Search all sections instead
                </button>
              )}
            </div>
          ) : (
            filteredResults.map((r, idx) => {
              const { sectionTitle, accent } = resolveSectionAndAccent(r.slug, r.title);
              return (
              <button
                key={r.slug}
                type="button"
                role="option"
                aria-selected={idx === activeIdx}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => { onClose(); nav(`/docs/${r.slug}`); }}
                data-testid={`docs-cmdk-result-${idx}`}
                className={`qt-docs-cmdk-row ${idx === activeIdx ? "is-active" : ""}`}
                style={{ "--row-accent": accent }}
              >
                <div className="qt-docs-cmdk-row-main">
                  <div className="qt-docs-cmdk-row-title">{r.title}</div>
                  {r.body_excerpt && (
                    <div className="qt-docs-cmdk-row-excerpt">{r.body_excerpt}</div>
                  )}
                  <div className="qt-docs-cmdk-row-meta">
                    {sectionTitle && (
                      <>
                        <span
                          className="qt-mono"
                          data-testid={`docs-cmdk-section-${idx}`}
                          style={{
                            color: accent,
                            letterSpacing: "0.14em",
                            textTransform: "uppercase",
                            fontWeight: 700,
                            fontSize: 10,
                          }}
                        >
                          {sectionTitle}
                        </span>
                        <span style={{ color: "rgba(255,255,255,0.18)" }}>·</span>
                      </>
                    )}
                    <span className="qt-mono">/{r.slug}</span>
                    {r.tags?.length > 0 && (
                      <>
                        <span style={{ color: "rgba(255,255,255,0.18)" }}>·</span>
                        <Tag size={10} style={{ color: "var(--qt-text-3)" }} />
                        {r.tags.slice(0, 3).join(" · ")}
                      </>
                    )}
                  </div>
                </div>
                <ArrowRight
                  size={12}
                  style={{
                    color: idx === activeIdx ? accent : "var(--qt-text-3)",
                    flexShrink: 0,
                  }}
                />
              </button>
              );
            })
          )}
        </div>

        <div className="qt-docs-cmdk-footer">
          <span className="qt-mono">QorTrace Docs · Cmd+K</span>
          {source && (
            <span className="qt-mono" data-testid="docs-cmdk-source">
              search: <strong style={{ color: "var(--qt-cyan)" }}>{source}</strong>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
